import 'ol/ol.css';
import './style.css';

import Map from 'ol/Map.js';
import View from 'ol/View.js';
import TileLayer from 'ol/layer/Tile.js';
import XYZ from 'ol/source/XYZ.js';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import Feature from 'ol/Feature.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import { fromExtent as polygonFromExtent } from 'ol/geom/Polygon.js';
import { Style, Stroke, Fill } from 'ol/style.js';
import Draw, { createBox } from 'ol/interaction/Draw.js';
import { ScaleLine, defaults as defaultControls } from 'ol/control.js';
import { createEmpty, extend as extendExtent, isEmpty } from 'ol/extent.js';

import { listEvents, loadEvent } from './s3.js';
import { createCogLayer, defaultWorkers, maxWorkers } from './cog.js';
import { buildGdalClip } from './gdal.js';

// ---------------------------------------------------------------------------
// DOM handles
// ---------------------------------------------------------------------------
const el = (id) => document.getElementById(id);
const sideBody = el('side-body');
const sideTitle = el('side-title');
const sideSub = el('side-sub');
const backBtn = el('back-btn');
const statusEl = el('status');
const statusText = el('status-text');
const spinner = statusEl.querySelector('.spinner');
const thumbOverlayEl = el('thumb-overlay');
const thumbImg = el('thumb-img');
const thumbCap = el('thumb-cap');
const hintEl = el('hint');

const geojson = new GeoJSON();

// ---------------------------------------------------------------------------
// Map — EVERYTHING is EPSG:4326 so the COGs (also 4326) never get reprojected.
// ---------------------------------------------------------------------------
const view = new View({
  projection: 'EPSG:4326',
  center: [0, 20],
  zoom: 2,
  maxZoom: 26,
  constrainResolution: false,
});

// Esri World Imagery (Web Mercator) — reprojected to the 4326 view by OL. This
// reprojection only ever touches the *basemap*; the COG path stays 1:1.
const imageryLayer = new TileLayer({
  zIndex: 0,
  source: new XYZ({
    url: 'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attributions:
      'Imagery © Esri, Maxar, Earthstar Geographics · Data: Vantor Open Data (CC-BY-NC-4.0)',
    maxZoom: 19,
    crossOrigin: 'anonymous',
  }),
});

const labelsLayer = new TileLayer({
  zIndex: 1,
  source: new XYZ({
    url: 'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
    maxZoom: 19,
    crossOrigin: 'anonymous',
  }),
});

// Event bbox rectangles (events view) and scene footprints (collection view).
const eventsSource = new VectorSource();
const eventsLayer = new VectorLayer({ zIndex: 5, source: eventsSource, style: eventStyle });

const footprintSource = new VectorSource();
const footprintLayer = new VectorLayer({ zIndex: 6, source: footprintSource, style: footprintStyle });

// Outline of the active scene (scene view) + the user-drawn clip box.
const outlineSource = new VectorSource();
const outlineLayer = new VectorLayer({
  zIndex: 20,
  source: outlineSource,
  style: new Style({ stroke: new Stroke({ color: '#34d3ff', width: 1.5 }) }),
});

const drawSource = new VectorSource();
const drawLayer = new VectorLayer({
  zIndex: 30,
  source: drawSource,
  style: new Style({
    stroke: new Stroke({ color: '#ffb454', width: 2 }),
    fill: new Fill({ color: 'rgba(255,180,84,0.12)' }),
  }),
});

const map = new Map({
  target: 'map',
  view,
  layers: [imageryLayer, labelsLayer, eventsLayer, footprintLayer, outlineLayer, drawLayer],
  controls: defaultControls().extend([new ScaleLine({ units: 'metric' })]),
});
const mapWrap = el('map-wrap');

// ---------------------------------------------------------------------------
// App state
// ---------------------------------------------------------------------------
const state = {
  view: 'events', // 'events' | 'collection' | 'scene'
  events: [],
  event: null,
  items: [],
  itemsById: new Map(),
  collection: null,
  collectionExtent: null,
  item: null,
  cog: null,
  workers: defaultWorkers(),
  hoverId: null,
  drawing: false,
  // pre/post classification
  eventDate: null, // 'YYYY-MM-DD' — splits scenes into pre/post by capture time
  stacInformative: false, // does the raw STAC `phase` field distinguish pre vs post?
  phaseFilter: 'all', // 'all' | 'pre' | 'post'
};
let openToken = 0; // guards against stale async scene opens

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const PHASE_COLOR = { pre: '#34d3ff', post: '#ffb454' };
const phaseColor = (p) => PHASE_COLOR[p] || '#3fb950';

// ---------------------------------------------------------------------------
// Pre/post classification
//
// The STAC `phase` field is unreliable on some events (e.g. every Venezuela
// scene is tagged "pre"). So phase is derived from an *event date* compared
// against each scene's capture time. The event date is user-editable and, when
// the STAC field is uninformative, auto-seeded from the largest temporal gap
// between captures (which is where a pre/post boundary almost always falls).
// We only ever label a scene "post" when that boundary is actually known —
// otherwise no pill is shown.
// ---------------------------------------------------------------------------
function phaseOf(item) {
  if (state.eventDate) {
    if (!item.datetime) return null;
    // ISO timestamps sort lexically, so a plain string compare is correct.
    return item.datetime >= state.eventDate ? 'post' : 'pre';
  }
  return state.stacInformative ? item.phase || null : null;
}

function hasBothPhases() {
  let pre = false;
  let post = false;
  for (const it of state.items) {
    const p = phaseOf(it);
    if (p === 'pre') pre = true;
    else if (p === 'post') post = true;
    if (pre && post) return true;
  }
  return false;
}

function isInformative(items) {
  let pre = false;
  let post = false;
  for (const it of items) {
    if (it.phase === 'pre') pre = true;
    else if (it.phase === 'post') post = true;
  }
  return pre && post;
}

const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };

// Vantor events are named like "Venezuela-Earthquake-Jun-2026" → use that
// month as the pre/post boundary signal.
function eventMonthStart(eventId) {
  const m = /(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*[-_\s]+(\d{4})/i.exec(eventId || '');
  if (!m) return null;
  return `${m[2]}-${String(MONTHS[m[1].toLowerCase().slice(0, 3)]).padStart(2, '0')}-01`;
}

// A sensible default event date: the first capture in/after the event's month
// (the response imagery), falling back to the largest temporal gap.
function defaultEventDate(items, eventId) {
  const dates = items.map((i) => i.datetime).filter(Boolean).sort();
  if (!dates.length) return null;

  const monthStart = eventMonthStart(eventId);
  if (monthStart) {
    const firstAfter = dates.find((d) => d >= monthStart);
    return (firstAfter || monthStart).slice(0, 10);
  }

  if (dates.length < 2) return null;
  let best = 0;
  let boundary = null;
  for (let k = 1; k < dates.length; k++) {
    const gap = Date.parse(dates[k]) - Date.parse(dates[k - 1]);
    if (gap > best) {
      best = gap;
      boundary = dates[k];
    }
  }
  return best < 2 * 24 * 3600 * 1000 ? null : boundary.slice(0, 10);
}

// Recompute every footprint's phase + filter visibility and restyle.
function applyPhasesToFootprints() {
  footprintSource.forEachFeature((f) => {
    const item = f.get('item');
    const p = phaseOf(item);
    f.set('phase', p);
    f.set('hidden', state.phaseFilter !== 'all' && p !== state.phaseFilter);
  });
  footprintLayer.changed();
}

function eventStyle(feature) {
  const hot = feature.getId() === state.hoverId;
  return new Style({
    stroke: new Stroke({ color: hot ? '#ffb454' : '#34d3ff', width: hot ? 2.5 : 1.5 }),
    fill: new Fill({ color: hot ? 'rgba(255,180,84,0.18)' : 'rgba(52,211,255,0.08)' }),
  });
}

function footprintStyle(feature) {
  if (feature.get('hidden')) return undefined; // filtered out by pre/post
  const c = phaseColor(feature.get('phase'));
  const hot = feature.getId() === state.hoverId;
  return new Style({
    stroke: new Stroke({ color: hot ? '#ffffff' : c, width: hot ? 3 : 1.5 }),
    fill: new Fill({ color: hot ? rgba(c, 0.28) : rgba(c, 0.08) }),
  });
}

function rgba(hex, a) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

// ---------------------------------------------------------------------------
// Small UI helpers
// ---------------------------------------------------------------------------
function setHeader(title, sub, showBack) {
  sideTitle.textContent = title;
  sideTitle.title = title;
  sideSub.textContent = sub;
  backBtn.hidden = !showBack;
}

function setStatus(show, text = '', spinning = false) {
  statusEl.hidden = !show;
  statusText.textContent = text;
  spinner.hidden = !spinning;
}

function setHint(html) {
  if (!html) {
    hintEl.hidden = true;
    return;
  }
  hintEl.hidden = false;
  hintEl.innerHTML = html;
}

const fmt = (v, unit = '', digits = 2) =>
  v === undefined || v === null || v === '' ? '—' : `${Number(v).toFixed(digits)}${unit}`;

function fmtBytes(n) {
  if (!n) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 ? 1 : 0)} ${u[i]}`;
}

function fmtDate(s) {
  if (!s) return '—';
  return s.replace('T', ' ').replace(/\.\d+Z?$/, '').replace('Z', '') + ' UTC';
}

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------
function featureFromItem(item) {
  let f;
  if (item.geometry) {
    f = geojson.readFeature(
      { type: 'Feature', geometry: item.geometry, properties: {} },
      { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:4326' }
    );
  } else if (item.bbox) {
    f = new Feature(polygonFromExtent(item.bbox));
  } else {
    return null;
  }
  f.setId(item.id);
  f.set('item', item);
  f.set('phase', item.phase);
  return f;
}

function itemExtent(item) {
  if (item.bbox) return item.bbox.slice();
  const f = featureFromItem(item);
  return f ? f.getGeometry().getExtent() : null;
}

// ---------------------------------------------------------------------------
// VIEW: events list
// ---------------------------------------------------------------------------
async function showEvents() {
  state.view = 'events';
  state.event = null;
  setHeader('Vantor Open Data', 'STAC Explorer · select an event', false);
  setHint(null);
  setStatus(false);
  teardownScene();
  footprintLayer.setVisible(false);
  footprintSource.clear();
  eventsLayer.setVisible(true);

  if (!state.events.length) {
    sideBody.innerHTML = `<div class="loading-msg">Loading events…</div>`;
    try {
      state.events = await listEvents();
    } catch (e) {
      sideBody.innerHTML = `<div class="error-msg">Could not list events.<br>${e.message}</div>`;
      return;
    }
  }
  renderEventList();
  await drawEventRectangles();
}

function renderEventList() {
  sideBody.innerHTML = '';
  for (const ev of state.events) {
    const row = document.createElement('div');
    row.className = 'list-item';
    row.dataset.id = ev.id;
    row.innerHTML = `
      <div class="thumb event">🛰️</div>
      <div class="li-text">
        <div class="li-title">${ev.name}</div>
        <div class="li-meta">${ev.id}</div>
      </div>`;
    row.addEventListener('click', () => openEvent(ev));
    row.addEventListener('mouseenter', () => setHover(ev.id));
    row.addEventListener('mouseleave', () => setHover(null));
    sideBody.appendChild(row);
  }
}

async function drawEventRectangles() {
  eventsSource.clear();
  const combined = createEmpty();
  await Promise.all(
    state.events.map(async (ev) => {
      try {
        if (!ev.bbox) {
          const res = await fetch(ev.collectionUrl, { mode: 'cors' });
          const col = await res.json();
          ev.bbox = col?.extent?.spatial?.bbox?.[0] || null;
          ev.description = col?.description || '';
          ev.title = col?.title || '';
        }
      } catch {
        /* ignore; rectangle just won't show */
      }
      if (ev.bbox) {
        const f = new Feature(polygonFromExtent(ev.bbox));
        f.setId(ev.id);
        f.set('event', ev);
        eventsSource.addFeature(f);
        extendExtent(combined, ev.bbox);
      }
    })
  );
  if (!isEmpty(combined)) {
    view.fit(combined, { padding: [60, 60, 60, 60], duration: 400, maxZoom: 9 });
  }
}

// ---------------------------------------------------------------------------
// VIEW: collection (scene footprints)
// ---------------------------------------------------------------------------
async function openEvent(ev) {
  state.view = 'collection';
  state.event = ev;
  setHover(null);
  setHeader(ev.name, 'Loading scenes…', true);
  setHint(null);
  eventsLayer.setVisible(false);
  footprintLayer.setVisible(true);
  footprintSource.clear();
  sideBody.innerHTML = `<div class="loading-msg">Loading scenes…</div>`;

  let data;
  try {
    data = await loadEvent(ev);
  } catch (e) {
    sideBody.innerHTML = `<div class="error-msg">Could not load this event.<br>${e.message}</div>`;
    return;
  }
  if (state.view !== 'collection' || state.event !== ev) return; // navigated away

  state.items = data.items;
  state.collection = data.collection;
  state.itemsById = new Map(data.items.map((it) => [it.id, it]));
  state.stacInformative = isInformative(data.items);
  state.eventDate = state.stacInformative ? null : defaultEventDate(data.items, ev.id);
  state.phaseFilter = 'all';

  const combined = createEmpty();
  for (const item of data.items) {
    const f = featureFromItem(item);
    if (f) footprintSource.addFeature(f);
    const ex = itemExtent(item);
    if (ex) extendExtent(combined, ex);
  }
  state.collectionExtent = isEmpty(combined) ? null : combined;
  applyPhasesToFootprints();

  renderCollectionSidebar();
  if (state.collectionExtent) {
    view.fit(state.collectionExtent, { padding: [50, 50, 50, 50], duration: 400 });
  }
}

function renderCollectionSidebar() {
  const ev = state.event;
  const collection = state.collection;
  const items = state.items;
  const both = hasBothPhases();
  const visible = items.filter((it) => state.phaseFilter === 'all' || phaseOf(it) === state.phaseFilter);

  setHeader(ev.name, `${items.length} scene${items.length === 1 ? '' : 's'}`, true);
  const desc = collection?.description || ev.description || '';
  const time = collection?.extent?.temporal?.interval?.[0] || [];

  const head = document.createElement('div');
  head.className = 'block';
  head.innerHTML = `
    ${desc ? `<p class="desc">${desc}</p>` : ''}
    ${
      time[0]
        ? `<dl class="kv" style="margin-top:8px">
             <dt>Window</dt><dd>${(time[0] || '').slice(0, 10)} → ${(time[1] || '').slice(0, 10)}</dd>
           </dl>`
        : ''
    }
    <label class="tiny muted" style="display:block;margin-top:10px">
      Event date <span class="muted">— splits scenes into pre / post</span>
    </label>
    <input type="date" class="date-input" id="event-date" value="${state.eventDate || ''}">
    ${
      both
        ? `<div class="seg" id="phase-filter">
             <button data-f="all" class="${state.phaseFilter === 'all' ? 'active' : ''}">All</button>
             <button data-f="pre" class="${state.phaseFilter === 'pre' ? 'active' : ''}">Pre</button>
             <button data-f="post" class="${state.phaseFilter === 'post' ? 'active' : ''}">Post</button>
           </div>`
        : ''
    }
    <p class="tiny muted" style="margin:10px 0 0">Click a footprint or a scene below to stream its COG.</p>`;
  sideBody.innerHTML = '';
  sideBody.appendChild(head);

  for (const item of visible) {
    const ph = phaseOf(item);
    const row = document.createElement('div');
    row.className = 'list-item';
    row.dataset.id = item.id;
    const phaseBadge = ph ? `<span class="badge ${ph}">${ph}</span> ` : '';
    row.innerHTML = `
      <img class="thumb" loading="lazy" src="${item.thumbUrl}" alt=""
           onerror="this.style.visibility='hidden'">
      <div class="li-text">
        <div class="li-title">${phaseBadge}${item.sensor || item.id}</div>
        <div class="li-meta">
          ${fmtDate(item.datetime).slice(0, 16)}<br>
          ${item.gsd ? `${fmt(item.gsd, ' m', 2)} GSD · ` : ''}${
      item.cloud != null ? `${item.cloud}% cloud` : ''
    }
        </div>
      </div>`;
    row.addEventListener('click', () => openScene(item));
    row.addEventListener('mouseenter', () => {
      setHover(item.id);
      showThumbForItem(item);
    });
    row.addEventListener('mouseleave', () => {
      setHover(null);
      hideThumb();
    });
    sideBody.appendChild(row);
  }

  el('event-date').addEventListener('change', (e) => {
    state.eventDate = e.target.value || null;
    state.phaseFilter = 'all';
    applyPhasesToFootprints();
    renderCollectionSidebar();
  });
  const filter = el('phase-filter');
  if (filter) {
    filter.querySelectorAll('button').forEach((btn) =>
      btn.addEventListener('click', () => {
        state.phaseFilter = btn.dataset.f;
        applyPhasesToFootprints();
        renderCollectionSidebar();
      })
    );
  }
}

// ---------------------------------------------------------------------------
// VIEW: scene (stream the COG)
// ---------------------------------------------------------------------------
async function openScene(item) {
  const token = ++openToken;
  state.view = 'scene';
  state.item = item;
  setHover(null);
  hideThumb();
  footprintLayer.setVisible(false);
  eventsLayer.setVisible(false);

  // Outline just this scene's footprint over the imagery while the COG loads.
  outlineSource.clear();
  const of = featureFromItem(item);
  if (of) outlineSource.addFeature(of);
  outlineLayer.setVisible(true);

  const ex = itemExtent(item);
  if (ex) view.fit(ex, { padding: [40, 40, 40, 40], duration: 350 });

  renderSceneSidebar(item);
  setHint(`<kbd>Esc</kbd> back to footprints · draw a box to export a clip`);
  setStatus(true, 'Opening COG…', true);

  teardownCog();
  try {
    const cog = await createCogLayer(item.cogUrl, {
      workers: state.workers,
      onProgress: ({ loading }) => {
        if (token !== openToken) return;
        if (loading > 0) setStatus(true, `Streaming ${loading} tile${loading === 1 ? '' : 's'}…`, true);
        else setStatus(true, `✓ Live · ${state.workers} workers · 0 reprojection`, false);
      },
    });
    if (token !== openToken) {
      cog.dispose();
      return; // user already navigated elsewhere
    }
    state.cog = cog;
    cog.layer.setZIndex(10);
    map.addLayer(cog.layer);
    view.fit(cog.extent, { padding: [40, 40, 40, 40], duration: 350 });
    setStatus(true, `✓ Live · ${state.workers} workers · 0 reprojection`, false);
  } catch (e) {
    if (token === openToken) setStatus(true, `Failed to open COG: ${e.message}`, false);
  }
}

function renderSceneSidebar(item) {
  setHeader(item.sensor || item.id, item.id, true);
  const ph = phaseOf(item);
  sideBody.innerHTML = '';

  const meta = document.createElement('div');
  meta.className = 'block';
  meta.innerHTML = `
    ${ph ? `<span class="badge ${ph}">${ph}-event</span>` : ''}
    <dl class="kv" style="margin-top:10px">
      <dt>Captured</dt><dd>${fmtDate(item.datetime)}</dd>
      <dt>Platform</dt><dd>${item.sensor || '—'}${
    item.constellation ? ` · ${item.constellation}` : ''
  }</dd>
      <dt>Pan GSD</dt><dd>${fmt(item.gsd, ' m')}</dd>
      <dt>Off-nadir</dt><dd>${fmt(item.offNadir, '°', 1)}</dd>
      <dt>Sun elev.</dt><dd>${fmt(item.sunElev, '°', 1)}</dd>
      <dt>Cloud</dt><dd>${item.cloud != null ? item.cloud + '%' : '—'}</dd>
      <dt>COG size</dt><dd>${fmtBytes(item.cogSize)}</dd>
    </dl>`;
  sideBody.appendChild(meta);

  // COG streaming controls
  const ctl = document.createElement('div');
  ctl.className = 'block';
  ctl.innerHTML = `
    <h2>COG streaming</h2>
    <div class="ctl">
      <label>Decode workers <span id="w-val">${state.workers}</span></label>
      <input type="range" id="w-range" min="1" max="${maxWorkers}" value="${state.workers}">
    </div>
    <div class="ctl">
      <label>Layer opacity <span id="o-val">100%</span></label>
      <input type="range" id="o-range" min="0" max="100" value="100">
    </div>
    <p class="tiny muted">Native EPSG:4326 tiles · no reprojection · ${
      item.cogSize ? fmtBytes(item.cogSize) + ' streamed on demand' : 'streamed on demand'
    }.</p>`;
  sideBody.appendChild(ctl);

  el('w-range').addEventListener('input', (e) => {
    state.workers = Number(e.target.value);
    el('w-val').textContent = state.workers;
    state.cog?.setWorkers(state.workers);
    if (!statusEl.hidden && spinner.hidden)
      setStatus(true, `✓ Live · ${state.workers} workers · 0 reprojection`, false);
  });
  el('o-range').addEventListener('input', (e) => {
    const o = Number(e.target.value);
    el('o-val').textContent = `${o}%`;
    state.cog?.setOpacity(o / 100);
  });

  // Clip / export
  const clip = document.createElement('div');
  clip.className = 'block';
  clip.innerHTML = `
    <h2>Export a clip (GDAL)</h2>
    <button class="btn primary" id="draw-btn">▭ Draw clip box</button>
    <div id="clip-out" class="cmd-wrap"></div>`;
  sideBody.appendChild(clip);
  el('draw-btn').addEventListener('click', toggleDraw);
}

// ---------------------------------------------------------------------------
// Drawing a clip box → GDAL command
// ---------------------------------------------------------------------------
let drawInteraction = null;

function toggleDraw() {
  if (state.drawing) stopDraw();
  else startDraw();
}

function startDraw() {
  stopDraw();
  drawSource.clear();
  // freehand: true → a single click-drag-release draws the box (natural gesture).
  drawInteraction = new Draw({
    source: drawSource,
    type: 'Circle',
    geometryFunction: createBox(),
    freehand: true,
  });
  drawInteraction.on('drawstart', () => drawSource.clear());
  drawInteraction.on('drawend', (e) => {
    const extent = e.feature.getGeometry().getExtent(); // [minLon,minLat,maxLon,maxLat] (4326)
    showGdal(extent);
    state.drawing = false;
    setDrawActive(false);
    // remove the interaction after the shape is committed
    setTimeout(stopDrawInteractionOnly, 0);
  });
  map.addInteraction(drawInteraction);
  state.drawing = true;
  setDrawActive(true);
  setHint(`Click-drag a box over the area to clip · <kbd>Esc</kbd> to cancel`);
}

function stopDrawInteractionOnly() {
  if (drawInteraction) {
    map.removeInteraction(drawInteraction);
    drawInteraction = null;
  }
}

function stopDraw() {
  stopDrawInteractionOnly();
  state.drawing = false;
  setDrawActive(false);
  if (state.view === 'scene')
    setHint(`<kbd>Esc</kbd> back to footprints · draw a box to export a clip`);
}

function setDrawActive(active) {
  const btn = el('draw-btn');
  if (!btn) return;
  btn.classList.toggle('active', active);
  btn.textContent = active ? '▭ Drawing… (Esc to cancel)' : '▭ Draw clip box';
}

function showGdal(extent) {
  const item = state.item;
  const finest = state.cog?.resolutions?.at(-1);
  const { html, command, dims } = buildGdalClip({
    cogUrl: item.cogUrl,
    sceneId: item.id,
    extent,
    resolution: finest,
  });

  const out = el('clip-out');
  out.innerHTML = `
    <p class="tiny muted" style="margin:6px 0">
      Clip ${dims ? `≈ ${dims.w.toLocaleString()} × ${dims.h.toLocaleString()} px · ` : ''}
      run locally to save a COG of the highlighted area:
    </p>
    <pre class="cmd">${html}</pre>
    <div class="copy-row">
      <button class="btn" id="copy-btn">Copy command</button>
      <button class="btn" id="clear-btn">Clear box</button>
    </div>`;
  el('copy-btn').addEventListener('click', () => copyText(command, el('copy-btn')));
  el('clear-btn').addEventListener('click', () => {
    drawSource.clear();
    out.innerHTML = '';
  });
}

async function copyText(text, btn) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    ta.remove();
  }
  const old = btn.textContent;
  btn.textContent = 'Copied ✓';
  btn.classList.add('copied');
  setTimeout(() => {
    btn.textContent = old;
    btn.classList.remove('copied');
  }, 1500);
}

// ---------------------------------------------------------------------------
// Hover + thumbnail overlay
// ---------------------------------------------------------------------------
function setHover(id) {
  if (state.hoverId === id) return;
  state.hoverId = id;
  eventsLayer.changed();
  footprintLayer.changed();
}

let thumbPixel = null;

// Position the thumbnail box near `pixel` (map-viewport coords) but always
// fully inside the map container — clamped on every edge, flipped below the
// point when there isn't room above.
function positionThumb(pixel) {
  if (!pixel) return;
  thumbPixel = pixel;
  const W = mapWrap.clientWidth;
  const H = mapWrap.clientHeight;
  const pw = thumbOverlayEl.offsetWidth || 210;
  const ph = thumbOverlayEl.offsetHeight || 160;
  const m = 12;
  let left = pixel[0] - pw / 2;
  let top = pixel[1] - ph - 16; // above the point by default
  if (top < m) top = pixel[1] + 20; // no room above → drop below
  left = Math.max(m, Math.min(left, W - pw - m));
  top = Math.max(m, Math.min(top, H - ph - m));
  thumbOverlayEl.style.left = `${left}px`;
  thumbOverlayEl.style.top = `${top}px`;
}

function showThumb(item, pixel) {
  if (state.view !== 'collection') return;
  thumbImg.onerror = hideThumb;
  // Reposition once the real image size is known so a tall thumb can't overflow.
  thumbImg.onload = () => positionThumb(thumbPixel);
  if (thumbImg.getAttribute('src') !== item.thumbUrl) thumbImg.src = item.thumbUrl;
  const ph = phaseOf(item);
  thumbCap.textContent = `${item.sensor || item.id}${ph ? ' · ' + ph : ''}`;
  thumbOverlayEl.classList.add('show');
  positionThumb(pixel);
}

function showThumbForItem(item) {
  const ex = itemExtent(item);
  if (!ex) return;
  const center = [(ex[0] + ex[2]) / 2, (ex[1] + ex[3]) / 2];
  const pixel = map.getPixelFromCoordinate(center) || [mapWrap.clientWidth / 2, mapWrap.clientHeight / 3];
  showThumb(item, pixel);
}

function hideThumb() {
  thumbOverlayEl.classList.remove('show');
}

// ---------------------------------------------------------------------------
// Map pointer interaction
// ---------------------------------------------------------------------------
map.on('pointermove', (evt) => {
  if (evt.dragging) return;
  const layer = state.view === 'events' ? eventsLayer : state.view === 'collection' ? footprintLayer : null;
  if (!layer) {
    map.getTargetElement().style.cursor = '';
    return;
  }
  let hit = null;
  map.forEachFeatureAtPixel(evt.pixel, (f) => (hit = f), { layerFilter: (l) => l === layer, hitTolerance: 2 });
  map.getTargetElement().style.cursor = hit ? 'pointer' : '';
  setHover(hit ? hit.getId() : null);

  if (state.view === 'collection') {
    const item = hit?.get('item');
    if (item) showThumb(item, evt.pixel);
    else hideThumb();
  }
});

map.on('click', (evt) => {
  if (state.drawing) return;
  if (state.view === 'events') {
    map.forEachFeatureAtPixel(
      evt.pixel,
      (f) => {
        const ev = f.get('event');
        if (ev) openEvent(ev);
        return true;
      },
      { layerFilter: (l) => l === eventsLayer }
    );
  } else if (state.view === 'collection') {
    map.forEachFeatureAtPixel(
      evt.pixel,
      (f) => {
        const item = f.get('item');
        if (item) openScene(item);
        return true;
      },
      { layerFilter: (l) => l === footprintLayer }
    );
  }
});

// ---------------------------------------------------------------------------
// Teardown helpers
// ---------------------------------------------------------------------------
function teardownCog() {
  if (state.cog) {
    map.removeLayer(state.cog.layer);
    state.cog.dispose();
    state.cog = null;
  }
}

function teardownScene() {
  teardownCog();
  stopDraw();
  drawSource.clear();
  outlineSource.clear();
  outlineLayer.setVisible(false);
}

// ---------------------------------------------------------------------------
// Back / Escape navigation
// ---------------------------------------------------------------------------
function back() {
  if (state.view === 'scene') {
    teardownScene();
    setStatus(false);
    state.view = 'collection';
    footprintLayer.setVisible(true);
    applyPhasesToFootprints();
    renderCollectionSidebar();
    setHint(null);
    if (state.collectionExtent)
      view.fit(state.collectionExtent, { padding: [50, 50, 50, 50], duration: 350 });
  } else if (state.view === 'collection') {
    showEvents();
  }
}

backBtn.addEventListener('click', back);

document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  if (state.drawing) {
    stopDraw();
    return;
  }
  back();
});

el('labels-toggle').addEventListener('change', (e) => labelsLayer.setVisible(e.target.checked));

// ---------------------------------------------------------------------------
// Go
// ---------------------------------------------------------------------------
showEvents();
